import type { LatencyBucket, ReasonCategory } from "./eventTypes";

export const OPERATIONAL_QUEUE_TTL_MS = 7 * 24 * 60 * 60 * 1_000;
const STORAGE_KEY = "pp_product_operational_queue_v1";
const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const REASONS = new Set<ReasonCategory>([
  "credentials_rejected", "provider_cancelled", "provider_unavailable", "network", "cors", "timeout",
  "unauthorized", "forbidden", "route_load", "unknown",
]);
const EVENTS = new Set([
  "login_started", "login_failed", "product_route_error", "cors_blocked",
  "analytics_delivery_attempted", "analytics_delivery_acknowledged",
]);

type OperationalInput = {
  event: "login_started" | "login_failed" | "product_route_error" | "cors_blocked"
    | "analytics_delivery_attempted" | "analytics_delivery_acknowledged";
  clientEventId?: string;
  navigationId?: string;
  loginAttemptId?: string;
  routeTemplate?: string;
  apiRouteFamily?: string;
  reasonCategory?: ReasonCategory;
  latencyBucket?: LatencyBucket;
  methodCategory?: "password" | "google" | "auth0_sso" | "other_provider";
};
type OperationalPayload = Omit<OperationalInput, "clientEventId"> & {
  clientEventId: string;
  occurredAt: string;
  host: "app";
  artifactSha: string;
};
type QueueRecord = { queueId: string; createdAt: number; payload: OperationalPayload };
// `NonNullable<Parameters<typeof fetch>[1]>` is the identical ambient
// `RequestInit` type; referencing that name directly trips `no-undef`, which
// cannot see TypeScript's ambient lib.dom.d.ts globals.
type FetchInit = NonNullable<Parameters<typeof fetch>[1]>;
type Request = (url: string, init: FetchInit) => Promise<Pick<Response, "ok" | "status">>;

function checkedOrigin(candidate: string) {
  let parsed: URL;
  try { parsed = new URL(candidate); } catch { throw new Error("Operational telemetry API origin is invalid"); }
  if (!/^https?:$/.test(parsed.protocol) || parsed.pathname !== "/" || parsed.search || parsed.hash || parsed.username || parsed.password) {
    throw new Error("Operational telemetry API origin is invalid");
  }
  const loopback = ["localhost", "127.0.0.1", "[::1]"].includes(parsed.hostname.toLowerCase());
  if (parsed.protocol !== "https:" && !loopback) throw new Error("HTTPS is required for the operational telemetry API origin");
  return parsed.origin;
}

// A raw control-character regex charset trips `no-control-regex` even when
// written via `\u00xx` escapes (the rule inspects the pattern's resulting
// code points, not the source spelling). Check char codes directly instead --
// identical matching semantics, no control-character regex range.
function hasReservedPathChar(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const char = value[i];
    const code = char.charCodeAt(0);
    if (char === "?" || char === "#" || char === "\\" || code <= 0x1f) return true;
  }
  return false;
}

function checkedPath(value: string | undefined, family: "route" | "api") {
  if (!value) return undefined;
  const prefix = family === "api" ? "/api/" : "/";
  const max = family === "api" ? 160 : 160;
  if (!value.startsWith(prefix) || value.startsWith("//") || value.length > max || hasReservedPathChar(value)) {
    throw new Error(`Invalid operational ${family} path`);
  }
  return value;
}

function closedPayload(input: OperationalInput, artifactSha: string, now: number, clientEventId: string): OperationalPayload {
  if (!EVENTS.has(input.event)) throw new Error("Invalid operational event");
  if (!UUID_V4.test(clientEventId)) throw new Error("Operational clientEventId must be UUIDv4");
  if (input.event === "analytics_delivery_attempted" || input.event === "analytics_delivery_acknowledged") {
    if (input.navigationId || input.loginAttemptId) throw new Error("Delivery audit uses only clientEventId grain");
    return {
      event: input.event, clientEventId, occurredAt: new Date(now).toISOString(), host: "app", artifactSha,
    };
  }
  const grain = input.navigationId ?? input.loginAttemptId;
  if (!grain || !UUID_V4.test(grain) || Boolean(input.navigationId) === Boolean(input.loginAttemptId)) {
    throw new Error("Exactly one UUIDv4 operational grain is required");
  }
  if ((input.event === "login_started" || input.event === "login_failed") && !input.loginAttemptId) {
    throw new Error("Operational login events require loginAttemptId");
  }
  if ((input.event === "login_started" || input.event === "login_failed")
    && !["password", "google", "auth0_sso", "other_provider"].includes(input.methodCategory ?? "")) {
    throw new Error("Operational login events require methodCategory");
  }
  if (input.event === "product_route_error" && !input.navigationId) {
    throw new Error("Operational route errors require navigationId");
  }
  if (input.reasonCategory && !REASONS.has(input.reasonCategory)) throw new Error("Invalid operational reason");
  const base = { event: input.event, clientEventId, occurredAt: new Date(now).toISOString(), host: "app" as const, artifactSha };
  const grainFields = input.navigationId ? { navigationId: input.navigationId } : { loginAttemptId: input.loginAttemptId };
  if (input.event === "login_started") return { ...base, ...grainFields, methodCategory: input.methodCategory };
  if (!input.reasonCategory) throw new Error("Operational failure requires reasonCategory");
  if (input.event === "login_failed") return { ...base, ...grainFields, methodCategory: input.methodCategory, reasonCategory: input.reasonCategory };
  if (input.event === "product_route_error") {
    if (!input.routeTemplate) throw new Error("Route error requires routeTemplate");
    return {
      ...base, ...grainFields, routeTemplate: checkedPath(input.routeTemplate, "route"), reasonCategory: input.reasonCategory,
    };
  }
  if (!input.apiRouteFamily) throw new Error("CORS failure requires apiRouteFamily");
  return {
    ...base, ...grainFields, apiRouteFamily: checkedPath(input.apiRouteFamily, "api"),
    reasonCategory: input.reasonCategory, ...(input.latencyBucket ? { latencyBucket: input.latencyBucket } : {}),
  };
}

function stable(value: unknown): string {
  if (!value || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stable).join(",")}]`;
  return `{${Object.entries(value).sort(([left], [right]) => left.localeCompare(right))
    .map(([key, item]) => `${JSON.stringify(key)}:${stable(item)}`).join(",")}}`;
}

function validateQueuedRecord(candidate: unknown, artifactSha: string): QueueRecord | null {
  if (!candidate || typeof candidate !== "object") return null;
  const record = candidate as Partial<QueueRecord>;
  if (!record.queueId || !UUID_V4.test(record.queueId) || typeof record.createdAt !== "number"
    || !Number.isFinite(record.createdAt) || !record.payload || typeof record.payload !== "object") return null;
  try {
    const expected = closedPayload(record.payload, artifactSha, record.createdAt, record.payload.clientEventId);
    if (stable(expected) !== stable(record.payload)) return null;
    return { queueId: record.queueId, createdAt: record.createdAt, payload: expected };
  } catch {
    return null;
  }
}

function readQueue(): QueueRecord[] {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
}

function writeQueue(records: QueueRecord[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(records.slice(-100)));
}

export function createOperationalTelemetry(options: {
  artifactSha: string;
  apiOrigin: string;
  request?: Request;
}) {
  if (!/^[0-9a-f]{64}$/.test(options.artifactSha)) throw new Error("A 64-character artifact SHA is required");
  const origin = checkedOrigin(options.apiOrigin);
  const request = options.request ?? fetch;

  async function flush(now = Date.now()) {
    let queue = readQueue().map((record) => validateQueuedRecord(record, options.artifactSha))
      .filter((record): record is QueueRecord => Boolean(record))
      .filter((record) => now >= record.createdAt && now - record.createdAt <= OPERATIONAL_QUEUE_TTL_MS);
    writeQueue(queue);
    for (const record of [...queue]) {
      try {
        const response = await request(`${origin}/api/analytics/operational`, {
          method: "POST", credentials: "omit", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(record.payload),
        });
        if (response.ok || (response.status >= 400 && response.status < 500 && response.status !== 429)) {
          queue = queue.filter((item) => item.queueId !== record.queueId);
          writeQueue(queue);
          continue;
        }
      } catch {
        // Network/CORS failures remain origin-local and retry after recovery.
      }
      break;
    }
  }

  return {
    async record(input: OperationalInput, now = Date.now()) {
      const clientEventId = input.clientEventId ?? crypto.randomUUID();
      const payload = closedPayload(input, options.artifactSha, now, clientEventId);
      const queue = readQueue();
      queue.push({ queueId: crypto.randomUUID(), createdAt: now, payload });
      writeQueue(queue);
      await flush(now);
    },
    flush,
    async pending() { return readQueue(); },
  };
}
